import { useStore } from 'vuex';
import { makeRequest } from '@/graphs/index';
import {
  myMailingAddress, organizationMailingAddress, updateAddress, updateOrganizationAddress,
} from '@/graphs/gql';
import withGraphErrors from '@/utils/graphqlResponse';
import { addressFromApi, addressToInput, type MailingAddress } from '@/utils/mailingAddress';

/** Plain words for the backend's address errors. */
export function addressErrorMessage(message: string | undefined, fallback: string): string {
  const text = (message || '').toLowerCase();
  if (text.includes('not authorized') || text.includes('unauthorized')) {
    return 'You can\'t change this organization\'s address. Ask one of its admins.';
  }
  if (text.includes('organization not found')) return 'This organization no longer exists.';
  return fallback;
}

/**
 * The mailing address (for tax receipts) of the signed-in user, or of the organization `orgId`
 * when given. Each call returns `{ error }` with a message ready to show.
 */
export function useMailingAddress() {
  const store = useStore();
  const token = () => store.getters['users/me']?.token ?? null;

  const load = async (
    orgId?: string | null,
  ): Promise<{ address?: MailingAddress; error?: string }> => {
    const response = await withGraphErrors(orgId
      ? makeRequest('query', organizationMailingAddress, { orgId }, { token: token() })
      : makeRequest('query', myMailingAddress, {}, { token: token() }));
    const value = orgId ? response?.data?.organization : response?.data?.profile;
    if (response?.errors?.length || !value) {
      return {
        error: addressErrorMessage(response?.errors?.[0]?.message, 'Couldn\'t load the mailing address.'),
      };
    }
    return { address: addressFromApi(value) };
  };

  const save = async (
    address: MailingAddress, orgId?: string | null,
  ): Promise<{ address?: MailingAddress; error?: string }> => {
    const input = addressToInput(address);
    const response = await withGraphErrors(orgId
      ? makeRequest('mutation', updateOrganizationAddress, { orgId, input }, { token: token() })
      : makeRequest('mutation', updateAddress, { input }, { token: token() }));
    const value = orgId
      ? response?.data?.updateOrganizationAddress
      : response?.data?.updateAddress;
    if (response?.errors?.length || !value) {
      return {
        error: addressErrorMessage(
          response?.errors?.[0]?.message,
          'Couldn\'t save the mailing address. Please try again.',
        ),
      };
    }
    return { address: addressFromApi(value) };
  };

  return { load, save };
}
